import React, { useState, useMemo, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useGlReplacements } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import { glReplacementService } from 'api/services/glReplacementService';
import type {
  GlReplacementOutgoingEntry,
  GlReplacementIncomingEntry,
} from 'api/types/glReplacement.types';

// ============================================================================
// TYPES
// ============================================================================

interface GlReplacementReportRecord {
  id: string;
  replacementRef: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  businessUnitName: string;
  totalOutgoingWeight: number;
  totalIncomingWeight: number;
  weightDifference: number;
  totalWeightBefore: number;
  settlementRatio: number;
  addonSettlementPrincipal: number;
  addonSettlementInterest: number;
  netCashFlow: number;
  statusCode: string;
  createdAt: string;
  submittedAt: string;
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

const GlReplacementReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // API hooks — reuse existing GL replacement hook
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useGlReplacements();
  const data = (apiData as unknown as GlReplacementReportRecord[]) || [];

  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Entry detail panel state (same pattern as Loan Mapping GL details)
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const entryCacheRef = useRef<Map<string, { outgoing: GlReplacementOutgoingEntry[]; incoming: GlReplacementIncomingEntry[] }>>(new Map());
  const [outgoingPanelRows, setOutgoingPanelRows] = useState<GlReplacementOutgoingEntry[]>([]);
  const [incomingPanelRows, setIncomingPanelRows] = useState<GlReplacementIncomingEntry[]>([]);
  const [entryPanelLoading, setEntryPanelLoading] = useState(false);
  const [entryTab, setEntryTab] = useState<'outgoing' | 'incoming'>('outgoing');

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.replacementRef} ${row.mappingRef} ${row.customerName} ${row.customerId} ${row.loanAccountNumber} ${row.businessUnitName} ${row.statusCode}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (statusFilter && row.statusCode !== statusFilter) return false;
      if (dateFrom) {
        const rowDate = (row.createdAt || '').split('T')[0];
        if (rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = (row.createdAt || '').split('T')[0];
        if (rowDate > dateTo) return false;
      }
      return true;
    });
  }, [data, searchQuery, statusFilter, dateFrom, dateTo]);

  // ============================================================================
  // ENTRY DETAIL PANEL HANDLER
  // ============================================================================

  const handleViewEntries = useCallback(async (recordId: string) => {
    if (expandedId === recordId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(recordId);
    setEntryTab('outgoing');
    if (entryCacheRef.current.has(recordId)) {
      const cached = entryCacheRef.current.get(recordId)!;
      setOutgoingPanelRows(cached.outgoing);
      setIncomingPanelRows(cached.incoming);
      return;
    }
    setEntryPanelLoading(true);
    try {
      const res = await glReplacementService.getById(recordId);
      const outgoing = res.data?.outgoingEntries ?? [];
      const incoming = res.data?.incomingEntries ?? [];
      entryCacheRef.current.set(recordId, { outgoing, incoming });
      setOutgoingPanelRows(outgoing);
      setIncomingPanelRows(incoming);
    } catch {
      showMessage('Failed to load entry details.', 'error');
      setExpandedId(null);
    } finally {
      setEntryPanelLoading(false);
    }
  }, [expandedId, showMessage]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<GlReplacementReportRecord>[] = useMemo(() => [
    { id: 'replacementRef', header: 'Replacement Ref', accessorKey: 'replacementRef', size: 160 },
    { id: 'mappingRef', header: 'Mapping Ref', accessorKey: 'mappingRef', size: 140 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 150 },
    { id: 'loanAccountNumber', header: 'Account #', accessorKey: 'loanAccountNumber', size: 140 },
    { id: 'businessUnitName', header: 'Branch', accessorKey: 'businessUnitName', size: 130, cell: ({ getValue }: any) => getValue() || '-' },
    {
      id: 'entries',
      header: 'Entries',
      accessorKey: 'id',
      size: 80,
      enableSorting: false,
      cell: ({ row }: any) => {
        const rowId = (row.original as GlReplacementReportRecord).id;
        const isExpanded = expandedId === rowId;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleViewEntries(rowId); }}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 28, height: 24, padding: '0 8px',
              borderRadius: 12, border: 'none', cursor: 'pointer',
              fontSize: '0.75rem', fontWeight: 700,
              background: isExpanded ? 'var(--primary)' : 'var(--primary-light, #e8f0fe)',
              color: isExpanded ? '#fff' : 'var(--primary)',
              transition: 'background 0.15s, color 0.15s',
            }}
            title={isExpanded ? 'Collapse entry details' : 'View entry details'}
          >
            <IconSystem.eye size={14} />
          </button>
        );
      },
    },
    {
      id: 'outgoingWeight',
      header: 'Out Wt (g)',
      accessorKey: 'totalOutgoingWeight',
      size: 100,
      cell: ({ getValue }: any) => <span>{(getValue() ?? 0).toFixed(3)}</span>,
    },
    {
      id: 'incomingWeight',
      header: 'In Wt (g)',
      accessorKey: 'totalIncomingWeight',
      size: 100,
      cell: ({ getValue }: any) => <span>{(getValue() ?? 0).toFixed(3)}</span>,
    },
    {
      id: 'weightDiff',
      header: 'Wt Diff (g)',
      accessorKey: 'weightDifference',
      size: 100,
      cell: ({ getValue }: any) => {
        const val = getValue() ?? 0;
        return <span style={{ fontWeight: 500, color: val > 0 ? 'var(--danger, #dc2626)' : 'var(--success, #16a34a)' }}>{val.toFixed(3)}</span>;
      },
    },
    {
      id: 'settlementRatio',
      header: 'Sett. Ratio',
      accessorKey: 'settlementRatio',
      size: 100,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 500 }}>{((getValue() ?? 0) * 100).toFixed(2)}%</span>,
    },
    {
      id: 'addonSettlementPrincipal',
      header: 'Sett. Principal',
      accessorKey: 'addonSettlementPrincipal',
      size: 140,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue() ?? 0)}</span>,
    },
    {
      id: 'addonSettlementInterest',
      header: 'Sett. Interest',
      accessorKey: 'addonSettlementInterest',
      size: 130,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue() ?? 0)}</span>,
    },
    {
      id: 'netCashFlow',
      header: 'Net Cash Flow',
      accessorKey: 'netCashFlow',
      size: 150,
      cell: ({ getValue }: any) => {
        const val = getValue() ?? 0;
        return <span style={{ fontWeight: 700, color: val >= 0 ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)' }}>{formatCurrency(val)}</span>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue() ?? '';
        return <span style={getStatusBadgeStyle(status)}>{status.replace(/_/g, ' ')}</span>;
      },
    },
    {
      id: 'createdAt',
      header: 'Created Date',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], [expandedId, handleViewEntries]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: 'Export',
      variant: 'primary',
      tooltip: 'Export Report',
      onClick: () => showMessage('Export functionality will be available after API integration.', 'info'),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="GL Replacement Report"
        icon={<IconSystem.refresh size={20} />}
        description="GL replacement transactions with weight analysis, settlement breakdown, and net cash flow."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search replacements...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* Grid wrapper — shrinks to make room for entry panel */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <SyutGrid
                data={filteredData}
                columns={tableColumns}
                enableRowSelection={false}
                enablePagination
                enableColumnFilters={false}
                emptyMessage="No GL replacements match the current filters."
              />
            </div>

            {/* Entry Detail Panel — fixed height, does not shrink */}
            {expandedId && (() => {
              const expandedRow = filteredData.find((r) => r.id === expandedId);
              return (
                <div style={{
                  flexShrink: 0,
                  borderTop: '2px solid var(--primary)',
                  background: 'var(--bg-primary, #fff)',
                  padding: '0.75rem 1rem',
                  maxHeight: 300,
                  overflow: 'auto',
                }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                <div>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>Replacement Entries</span>
                  {expandedRow && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                      — {expandedRow.replacementRef}
                      {expandedRow.customerName ? ` · ${expandedRow.customerName}` : ''}
                      {expandedRow.customerId ? ` (${expandedRow.customerId})` : ''}
                    </span>
                  )}
                </div>
                <button
                  className="search-panel-close"
                  onClick={() => setExpandedId(null)}
                  title="Close entry details"
                >
                  <IconSystem.x size={14} />
                </button>
              </div>

              {entryPanelLoading ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Loading entry details…
                </div>
              ) : outgoingPanelRows.length === 0 && incomingPanelRows.length === 0 ? (
                <div style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  No entries found for this replacement.
                </div>
              ) : (
                <div>
                  {/* Tab buttons */}
                  <div style={{ display: 'flex', gap: '0', marginBottom: '0.5rem', borderBottom: '2px solid var(--border-color, #e2e8f0)' }}>
                    <button
                      onClick={() => setEntryTab('outgoing')}
                      style={{
                        padding: '0.4rem 1rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        background: 'none', border: 'none',
                        color: entryTab === 'outgoing' ? '#dc2626' : 'var(--text-muted)',
                        borderBottom: entryTab === 'outgoing' ? '2px solid #dc2626' : '2px solid transparent',
                        marginBottom: '-2px',
                      }}
                    >
                      Outgoing GL ({outgoingPanelRows.length})
                    </button>
                    <button
                      onClick={() => setEntryTab('incoming')}
                      style={{
                        padding: '0.4rem 1rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        background: 'none', border: 'none',
                        color: entryTab === 'incoming' ? '#16a34a' : 'var(--text-muted)',
                        borderBottom: entryTab === 'incoming' ? '2px solid #16a34a' : '2px solid transparent',
                        marginBottom: '-2px',
                      }}
                    >
                      Incoming GL ({incomingPanelRows.length})
                    </button>
                  </div>

                  {/* Outgoing GL Table */}
                  {entryTab === 'outgoing' && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--table-header-bg, #f4f6f9)' }}>
                          {['#', 'GL Number', 'Customer', 'Weight (g)', 'Alloc. Ratio', 'Principal (₹)', 'Interest (₹)'].map((h) => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {outgoingPanelRows.map((entry, idx) => (
                          <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                            <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{entry.outgoingGlNumber || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{entry.outgoingCustomerName || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{Number(entry.outgoingNetWeight).toFixed(3)}</td>
                            <td style={{ padding: '6px 10px' }}>{((entry.outgoingAllocationRatio || 0) * 100).toFixed(2)}%</td>
                            <td style={{ padding: '6px 10px' }}>{formatCurrency(entry.outgoingGlOutstandingPrincipal)}</td>
                            <td style={{ padding: '6px 10px' }}>{formatCurrency(entry.outgoingGlOutstandingInterest)}</td>
                          </tr>
                        ))}
                        {outgoingPanelRows.length === 0 && (
                          <tr><td colSpan={7} style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No outgoing entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {/* Incoming GL Table */}
                  {entryTab === 'incoming' && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--table-header-bg, #f4f6f9)' }}>
                          {['#', 'GL Number', 'Customer', 'Weight (g)', 'Loan Amt (₹)', 'Loan Date'].map((h) => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {incomingPanelRows.map((entry, idx) => (
                          <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                            <td style={{ padding: '6px 10px', color: 'var(--text-muted)' }}>{idx + 1}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{entry.incomingGlNumber || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{entry.incomingCustomerName ? `${entry.incomingCustomerId} - ${entry.incomingCustomerName}` : (entry.incomingCustomerId || '—')}</td>
                            <td style={{ padding: '6px 10px' }}>{Number(entry.incomingNetWeight).toFixed(3)}</td>
                            <td style={{ padding: '6px 10px' }}>{formatCurrency(entry.incomingOriginalLoanAmount)}</td>
                            <td style={{ padding: '6px 10px' }}>{entry.incomingLoanDate ? entry.incomingLoanDate.split('T')[0] : '—'}</td>
                          </tr>
                        ))}
                        {incomingPanelRows.length === 0 && (
                          <tr><td colSpan={6} style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>No incoming entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })()}

          </div>
        )}

        {/* Filter Panel — only rendered when open */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'RETURNED', label: 'Returned' },
                    { value: 'REJECTED', label: 'Rejected' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Search status..."
                  isSearchable={true}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
                <input
                  type="date"
                  className="form-input"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{ fontSize: '0.8rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
                <input
                  type="date"
                  className="form-input"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{ fontSize: '0.8rem' }}
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlReplacementReportScreen;
